import { React, useState, useEffect } from "react";
import Header from "../../../Common/header";
import Footer from "../../../Common/footer";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { API } from "../../../Common/lib/api";
import { getUserSession } from "../../../Common/lib/Session";
import { MapContainer } from "react-leaflet";
import ProjectArea from "../../DataVisualization/MetaVisualization/MetaProjectAreaMap";
import ConfirmRevertModal from "./confirmRevertModal";

export default function EditComparison() {
  let history = useHistory();
  let { search } = useLocation();
  const query = new URLSearchParams(search);
  const logId = query.get("id");
  const [projectEdit, setProjectEdit] = useState();
  const [editState, setEditState] = useState("before");
  const [oldValues, setOldValues] = useState();
  const [newValues, setNewValues] = useState();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  useEffect(() => {
    /**
     * Retrieves the update log from the database.
     */
    const fetchData = async () => {
      try {
        const editQuery = await API.logs.get(logId);
        const logEntry = editQuery.data.getLog;

        // Defensive parsing: ensure we always set projectEdit and safe old/new values
        const safeParse = (str, fallback = {}) => {
          try {
            if (typeof str === 'string' && str.length > 0) return JSON.parse(str);
            return fallback;
          } catch (e) {
            return fallback;
          }
        };

        const toCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        const normalize = (obj) => {
          if (typeof obj !== 'string') {
            const out = {};
            const common = ['title','abstract','researchProgramName','research_program_name','website','keywords','vessels','citation','eastLongitude','east_longitude','westLongitude','west_longitude','northLatitude','north_latitude','southLatitude','south_latitude','stakeholders'];
            common.forEach((key) => {
              if (!(key in obj)) out[key] = obj[key] || '';
              else out[key] = obj[key];
            });
            return out;
          }
          const parsed = safeParse(obj, {});
          const out = {};
          Object.entries(parsed || {}).forEach(([k, v]) => {
            out[k] = v;
            try {
              const camel = toCamel(k);
              if (!(camel in out)) out[camel] = v;
            } catch (e) {}
          });
          // ensure common fields exist as strings to render
          const common = ['title','abstract','researchProgramName','research_program_name','website','keywords','vessels','citation','eastLongitude','east_longitude','westLongitude','west_longitude','northLatitude','north_latitude','southLatitude','south_latitude','stakeholders'];
          common.forEach((key) => {
            if (!(key in out)) out[key] = '';
          });
          return out;
        };

        setProjectEdit(logEntry || { fieldUpdated: [] });
        setOldValues(normalize(logEntry?.oldValue));
        setNewValues(normalize(logEntry?.newValue));
      } catch (error) {
        console.error('Error fetching edit log:', error);
        setProjectEdit({});
        setOldValues({});
        setNewValues({});
      }
    };

    fetchData();
  }, [logId]);

  /**
   * Reverts the project to the version before this edit took place.
   * @param {*} event
   */
  const revertChanges = async (event) => {
    const user = await getUserSession();
    if (user.is_staff) {
      try {
        const projectResult = await API.getProject(projectEdit.projectID, projectEdit.projectID);
        const project = projectResult.data.getProject;
        const editedFields = [];

        const parseArray = (val) => {
          try {
            if (Array.isArray(val)) return val;
            if (typeof val === 'string' && val.length > 0) return JSON.parse(val);
            return [];
          } catch (e) {
            return [];
          }
        };

        if (project.title !== oldValues.title) {
          editedFields.push("title");
        }

        if (project.abstract !== oldValues.abstract) {
          editedFields.push("abstract");
        }

        const projectStakeholders = parseArray(project.stakeholders);
        const oldStakeholders = parseArray(oldValues.stakeholders);
        projectStakeholders.forEach((stakeholder, index) => {
          if (
            oldStakeholders[index] && stakeholder.name !== oldStakeholders[index].name &&
            !editedFields.includes("stakeholders")
          ) {
            editedFields.push("stakeholders");
          }
        });

        if (project.researchProgramName !== oldValues.researchProgramName) {
          editedFields.push("researchProgramName");
        }

        if (project.website !== oldValues.website) {
          editedFields.push("website");
        }

        if (project.keywords !== oldValues.keywords) {
          editedFields.push("keywords");
        }

        if (project.eastLongitude !== oldValues.eastLongitude) {
          editedFields.push("eastLongitude");
        }

        if (project.westLongitude !== oldValues.westLongitude) {
          editedFields.push("westLongitude");
        }

        if (project.northLatitude !== oldValues.northLatitude) {
          editedFields.push("northLatitude");
        }

        if (project.southLatitude !== oldValues.southLatitude) {
          editedFields.push("southLatitude");
        }

        if (project.citation !== oldValues.citation) {
          editedFields.push("citation");
        }

        if (project.vessels !== oldValues.vessels) {
          editedFields.push("vessels");
        }

        await API.projects.update({
          id: project.id,
          userID: user.username,
          abstract: oldValues.abstract,
          citation: oldValues.citation,
          eastLongitude: parseFloat(oldValues.eastLongitude),
          keywords: oldValues.keywords,
          vessels: oldValues.vessels,
          northLatitude: parseFloat(oldValues.northLatitude),
          researchProgramName: oldValues.researchProgramName,
          southLatitude: parseFloat(oldValues.southLatitude),
          stakeholders: oldValues.stakeholders,
          title: oldValues.title,
          updatedAt: parseInt(Date.now()),
          website: oldValues.website,
          westLongitude: parseFloat(oldValues.westLongitude),
        });

        setTimeout(() => history.push("/admin/project-logs"), 1000);
      } catch (error) {
        // console.log(error);
      }
    }
  };

  return (
    <>
      <ConfirmRevertModal setOpenConfirmModal={setOpenConfirmModal} openConfirmModal={openConfirmModal} revertChanges={revertChanges}/>
      <Header />
      {projectEdit ? (
        <div className="lg:relative mb-24 mt-12 bg-white">
          <div className="relative max-w-2xl msm:max-w-lg overflow-hidden m-auto shadow-sm border bg-gray-100">
            <div className="max-w-7xl mx-auto">
              <div className="absolute right-0">
                <span className="isolate inline-flex rounded-md shadow-sm">
                  <button
                    type="button"
                    className={
                      editState === "before"
                        ? "relative -ml-px inline-flex items-center bg-blue px-3 py-2 text-sm font-semibold text-white ring-1 ring-inset ring-gray-300 focus:z-10 bg-blue-700"
                        : "relative -ml-px inline-flex items-center bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                    }
                    data-cy="before"
                    onClick={() => setEditState("before")}
                  >
                    Before
                  </button>
                  <button
                    type="button"
                    className={
                      editState === "after"
                        ? "relative -ml-px inline-flex items-center bg-blue px-3 py-2 text-sm font-semibold text-white ring-1 ring-inset ring-gray-300 focus:z-10 bg-blue-700"
                        : "relative -ml-px inline-flex items-center bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                    }
                    data-cy="after"
                    onClick={() => setEditState("after")}
                  >
                    After
                  </button>
                </span>
              </div>
              <h1 className="text-center font-extrabold text-gray-900 sm:text-xl sm:tracking-tight lg:text-3xl py-6 border-b-2">
                Edit Project - {editState === "before" ? "Before" : "After"}
              </h1>
              <section className="pt-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <form className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="py-3">
                      <span className="block text-base font-semibold text-gray-900">
                        Project Title {projectEdit.fieldUpdated.includes("title") ? " - Edited" : ""}
                      </span>
                      <span className="block text-base font-normal text-gray-900">
                        {editState === "before" ? oldValues.title : newValues.title}
                      </span>
                    </div>

                    <div className="py-3">
                      <span className="block text-base font-semibold text-gray-900">
                        Abstract {projectEdit.fieldUpdated.includes("abstract") ? " - Edited" : ""}
                      </span>
                      <span className="block text-base font-normal text-gray-900">
                        {editState === "before" ? oldValues.abstract : newValues.abstract}
                      </span>
                    </div>

                    {editState === "before" &&
                      (function() {
                        const parseArray = (val) => {
                          try {
                            if (Array.isArray(val)) return val;
                            if (typeof val === 'string' && val.length > 0) return JSON.parse(val);
                            return [];
                          } catch (e) {
                            return [];
                          }
                        };
                        const oldStakeholders = parseArray(oldValues.stakeholders);
                        const newStakeholders = parseArray(newValues.stakeholders);
                        return oldStakeholders.map((stakeholder, index) => {
                          let edited = false;
                          if (newStakeholders[index] && newStakeholders[index].name !== stakeholder.name) {
                            edited = true;
                          }
                          return (
                            <div className="py-3" key={index}>
                              <span className="block text-base font-semibold text-gray-900">
                                {stakeholder.role + (edited ? " - Edited" : "")}
                              </span>
                              <span className="block text-base font-normal text-gray-900">
                                {stakeholder.name}
                              </span>
                            </div>
                          );
                        });
                      })()}

                    {editState === "after" &&
                      (function() {
                        const parseArray = (val) => {
                          try {
                            if (Array.isArray(val)) return val;
                            if (typeof val === 'string' && val.length > 0) return JSON.parse(val);
                            return [];
                          } catch (e) {
                            return [];
                          }
                        };
                        const oldStakeholders = parseArray(oldValues.stakeholders);
                        const newStakeholders = parseArray(newValues.stakeholders);
                        return newStakeholders.map((stakeholder, index) => {
                          let edited = false;
                          if (oldStakeholders[index] && oldStakeholders[index].name !== stakeholder.name) {
                            edited = true;
                          }
                          return (
                            <div className="py-3" key={index}>
                              <span className="block text-base font-semibold text-gray-900">
                                {stakeholder.role + (edited ? " - Edited" : "")}
                              </span>
                              <span className="block text-base font-normal text-gray-900">
                                {stakeholder.name}
                              </span>
                            </div>
                          );
                        });
                      })()}

                    <div className="py-3">
                      <span className="block text-base font-semibold text-gray-900">
                        Research Program Name{" "}
                        {projectEdit.fieldUpdated.includes("researchProgramName") ? " - Edited" : ""}
                      </span>
                      <span className="block text-base font-normal text-gray-900">
                        {editState === "before"
                          ? oldValues.researchProgramName
                          : newValues.researchProgramName}
                      </span>
                    </div>

                    <div className="py-3">
                      <span className="block text-base font-semibold text-gray-900">
                        Website {projectEdit.fieldUpdated.includes("website") ? " - Edited" : ""}
                      </span>
                      <span className="block text-base font-normal text-gray-900">
                        {editState === "before" ? oldValues.website : newValues.website}
                      </span>
                    </div>

                    <div className="py-3">
                      <span className="block text-base font-semibold text-gray-900">
                        Keywords {projectEdit.fieldUpdated.includes("keywords") ? " - Edited" : ""}
                      </span>
                      <div className="mt-1 flex flex-wrap">
                        {editState === "before"
                          ? (function() {
                              try {
                                return (JSON.parse(oldValues.keywords || '[]') || []).map((keyword) => (
                                  <span
                                    key={keyword}
                                    className="mr-2 mb-2 px-3 py-1 rounded-full text-gray-500 bg-gray-200 font-semibold text-sm flex align-center w-max active:bg-gray-300 transition duration-300 ease"
                                  >
                                    {keyword}
                                  </span>
                                ));
                              } catch (e) {
                                return (oldValues.keywords || []).map((keyword) => (
                                  <span
                                    key={keyword}
                                    className="mr-2 mb-2 px-3 py-1 rounded-full text-gray-500 bg-gray-200 font-semibold text-sm flex align-center w-max active:bg-gray-300 transition duration-300 ease"
                                  >
                                    {keyword}
                                  </span>
                                ));
                              }
                            })()
                          : (Array.isArray(newValues.keywords) ? newValues.keywords : []).map((keyword) => {
                              return (
                                <span
                                  key={keyword}
                                  className="mr-2 mb-2 px-3 py-1 rounded-full text-gray-500 bg-gray-200 font-semibold text-sm flex align-center w-max active:bg-gray-300 transition duration-300 ease"
                                >
                                  {keyword}
                                </span>
                              );
                            })}
                      </div>
                    </div>

                    <div className="py-3">
                      <span className="block text-base font-semibold text-gray-900">
                        Project Area {projectEdit.fieldUpdated.includes("northLatitude") ? " - Edited" : ""}
                      </span>
                      <MapContainer
                        center={[69.116864, -105.059681]}
                        zoom={5}
                        style={{
                          height: "300px",
                          zIndex: 0,
                        }}
                        worldCopyJump={true}
                        maxBoundsViscosity={1}
                        minZoom={2}
                      >
                        <ProjectArea meta={editState === "before" ? oldValues : newValues} />
                      </MapContainer>
                    </div>

                    <div className="grid grid-cols-2 gap-1 w-full py-3 mt-2">
                      <div className="flex items-end justify-start">
                        <p className="bottom-0 mb-0 font-semibold text-sm ">Latitude</p>
                      </div>
                      <div className="flex items-end justify-start">
                        <p className="bottom-0 mb-0 font-semibold text-sm ">Longitude</p>
                      </div>

                      <span className="block text-base font-normal text-gray-900">
                        {editState === "before" ? oldValues.northLatitude : newValues.northLatitude}
                      </span>
                      <span className="block text-base font-normal text-gray-900">
                        {editState === "before" ? oldValues.eastLongitude : newValues.eastLongitude}
                      </span>

                      <span className="block text-base font-normal text-gray-900">
                        {editState === "before" ? oldValues.southLatitude : newValues.southLatitude}
                      </span>
                      <span className="block text-base font-normal text-gray-900">
                        {editState === "before" ? oldValues.westLongitude : newValues.westLongitude}
                      </span>
                    </div>

                    <div className="py-3">
                      <span className="block text-base font-semibold text-gray-900">
                        Citation {projectEdit.fieldUpdated.includes("citation") ? " - Edited" : ""}
                      </span>
                      <span className="block text-base font-normal text-gray-900">
                        {editState === "before" ? oldValues.citation : newValues.citation}
                      </span>
                    </div>

                    <div className="py-3">
                      <span className="block text-base font-semibold text-gray-900">
                        Vessels/Labs/Infrastructure Used{" "}
                        {projectEdit.fieldUpdated.includes("vessels") ? " - Edited" : ""}
                      </span>
                      <div className="mt-1 flex flex-wrap">
                        {editState === "before"
                          ? (function() {
                              try {
                                return (JSON.parse(oldValues.vessels || '[]') || []).map((vessel) => (
                                  <span
                                    key={vessel}
                                    className="mr-2 mb-2 px-3 py-1 rounded-full text-gray-500 bg-gray-200 font-semibold text-sm flex align-center w-max active:bg-gray-300 transition duration-300 ease"
                                  >
                                    {vessel}
                                  </span>
                                ));
                              } catch (e) {
                                return (oldValues.vessels || []).map((vessel) => (
                                  <span
                                    key={vessel}
                                    className="mr-2 mb-2 px-3 py-1 rounded-full text-gray-500 bg-gray-200 font-semibold text-sm flex align-center w-max active:bg-gray-300 transition duration-300 ease"
                                  >
                                    {vessel}
                                  </span>
                                ));
                              }
                            })()
                          : (Array.isArray(newValues.vessels) ? newValues.vessels : []).map((vessel) => {
                              return (
                                <span
                                  key={vessel}
                                  className="mr-2 mb-2 px-3 py-1 rounded-full text-gray-500 bg-gray-200 font-semibold text-sm flex align-center w-max active:bg-gray-300 transition duration-300 ease"
                                >
                                  {vessel}
                                </span>
                              );
                            })}
                      </div>
                    </div>

                    <div className="flex items-center justify-center my-10">
                      <span className="xl:px-20 mxl:px-20 msm:px-2 ">
                        <input
                          type="button"
                          onClick={() => setOpenConfirmModal(true)}
                          data-cy="Revert Changes"
                          value="Revert Changes"
                          className="bg-blue-500 hover:bg-blue-700 text-white font-bold  sm:text-lg sm:h-12 sm:w-48 mmd:text-sm mmd:w-32 msm:text-xs msm:h-8 rounded"
                        />
                      </span>
                    </div>
                  </form>
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : (
        <p></p>
      )}
      <Footer />
    </>
  );
}
